import React, { useState, useRef } from 'react';
import { motion } from 'framer-motion';
import styles from './MentorVideoHero.module.css';

const MentorVideoHero = () => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [showCaptions, setShowCaptions] = useState(false);
  const videoRef = useRef(null);

  const handlePlayPause = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const toggleCaptions = () => {
    setShowCaptions(!showCaptions);
    if (videoRef.current) {
      const tracks = videoRef.current.textTracks;
      if (tracks.length > 0) {
        tracks[0].mode = showCaptions ? 'hidden' : 'showing';
      }
    }
  };

  return (
    <section className={styles.heroSection}>
      <div className={styles.heroContainer}>
        <div className={styles.heroContent}>
          <motion.div
            className={styles.textContent}
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className={styles.heroTitle}>
              Get Straight-Talk From a Venice Biennale Artist
            </h1>
            <p className={styles.heroSubtext}>
              45 minutes of honest critique & career insight with Todd Williamson.
            </p>
          </motion.div>

          <motion.div
            className={styles.videoContainer}
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <div className={styles.videoWrapper}>
              {/* Video placeholder - replace with actual video source */}
              <video
                ref={videoRef}
                className={styles.video}
                poster="/api/placeholder/800/450"
                preload="metadata"
                onPlay={() => setIsPlaying(true)}
                onPause={() => setIsPlaying(false)}
                aria-label="Todd Williamson introduction video"
              >
                {/* Replace with actual video source */}
                <source src="/videos/todd-intro.mp4" type="video/mp4" />
                <track
                  kind="captions"
                  src="/captions/todd-intro-en.vtt"
                  srcLang="en"
                  label="English captions"
                  default
                />
                Your browser does not support the video tag.
              </video>

              {/* Video Overlay */}
              <div className={styles.videoOverlay}>
                <button
                  className={styles.playButton}
                  onClick={handlePlayPause}
                  aria-label={isPlaying ? 'Pause video' : 'Play video'}
                >
                  {isPlaying ? (
                    <svg className={styles.playIcon} viewBox="0 0 24 24" fill="currentColor">
                      <path d="M6 4h4v16H6zm8 0h4v16h-4z"/>
                    </svg>
                  ) : (
                    <svg className={styles.playIcon} viewBox="0 0 24 24" fill="currentColor">
                      <path d="M8 5v14l11-7z"/>
                    </svg>
                  )}
                </button>

                <div className={styles.videoControls}>
                  <button
                    className={styles.captionButton}
                    onClick={toggleCaptions}
                    aria-label={showCaptions ? 'Hide captions' : 'Show captions'}
                    title={showCaptions ? 'Hide captions' : 'Show captions'}
                  >
                    <svg className={styles.captionIcon} viewBox="0 0 24 24" fill="currentColor">
                      <path d="M19 4H5c-1.11 0-2 .9-2 2v12c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm-8 7H9.5v-.5h-2v3h2V13H11v1c0 .55-.45 1-1 1H7c-.55 0-1-.45-1-1v-4c0-.55.45-1 1-1h3c.55 0 1 .45 1 1v1zm7 0h-1.5v-.5h-2v3h2V13H18v1c0 .55-.45 1-1 1h-3c-.55 0-1-.45-1-1v-4c0-.55.45-1 1-1h3c.55 0 1 .45 1 1v1z"/>
                    </svg>
                  </button>
                </div>
              </div>

              {/* Placeholder for when video is not available */}
              <div className={styles.videoPlaceholder}>
                <div className={styles.placeholderContent}>
                  <div className={styles.placeholderIcon}>
                    <svg viewBox="0 0 24 24" fill="currentColor">
                      <path d="M8 5v14l11-7z"/>
                    </svg>
                  </div>
                  <p className={styles.placeholderText}>
                    Introduction Video Coming Soon
                  </p>
                  <p className={styles.placeholderSubtext}>
                    Preview of Todd discussing his mentorship approach
                  </p>
                </div>
              </div>
            </div>

            {/* Alternative: YouTube/Vimeo embed placeholder */}
            <div className={styles.embedPlaceholder}>
              <div className={styles.embedContainer}>
                <iframe
                  src="https://www.youtube.com/embed/dQw4w9WgXcQ?controls=1&showinfo=0&rel=0"
                  title="Todd Williamson Mentorship Introduction"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  className={styles.embedVideo}
                />
              </div>
              <p className={styles.embedNote}>
                * Replace with actual Todd Williamson introduction video
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default MentorVideoHero; 